import { ColumnDef } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { DataTableColumnHeader } from '@/components/shared/data-table/data-table-column-header'
import { format } from 'date-fns'
import { useLanguage } from '@/context/LanguageContext'
import { ContentBlock, ContentBlockType } from '../types'

const typeLabels: Record<ContentBlockType, string> = {
  only_text: 'Text Only',
  only_image: 'Image Only',
  text_and_image: 'Text & Image',
  only_video: 'Video Only',
  text_and_video: 'Text & Video',
  gallery: 'Gallery',
  button: 'Button',
  photo_links: 'Photo Links',
  category_tiles: 'Category Tiles',
  product_grid: 'Product Grid',
  page_grid: 'Page Grid',
  email_signup: 'Email Signup',
  pdf_flipbook: 'PDF Flipbook',
}

const typeVariants: Record<ContentBlockType, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  only_text: 'secondary',
  only_image: 'default',
  text_and_image: 'default',
  only_video: 'outline',
  text_and_video: 'outline',
  gallery: 'default',
  button: 'secondary',
  photo_links: 'default',
  category_tiles: 'default',
  product_grid: 'default',
  page_grid: 'default',
  email_signup: 'secondary',
  pdf_flipbook: 'outline',
}

function NameCell({ row }: { row: { original: ContentBlock } }) {
  const { language } = useLanguage()
  const profiles = row.original.content_block_profiles
  const profile = profiles.find(p => p.language === language) || profiles[0]

  return (
    <div className="flex space-x-2">
      <span className="max-w-[500px] truncate font-medium">
        {profile?.name}
      </span>
    </div>
  )
}

function PageCell({ row }: { row: { original: ContentBlock } }) {
  const { language } = useLanguage()
  const page = row.original.page
  if (!page) return <span className="text-muted-foreground">—</span>

  const profile = page.page_profiles.find(p => p.language === language) || page.page_profiles[0]
  const label = profile?.title || page.code

  return (
    <span className="text-sm text-muted-foreground">{label}</span>
  )
}

export const columns: ColumnDef<ContentBlock>[] = [
  {
    accessorKey: 'id',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Id" />
    ),
    cell: ({ row }) => (
      <div className="text-xs text-muted-foreground">{row.getValue('id')}</div>
    ),
    meta: { width: '50px' },
  },
  {
    id: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => <NameCell row={row} />,
  },
  {
    accessorKey: 'type',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    cell: ({ row }) => {
      const type = row.getValue('type') as ContentBlockType
      return (
        <Badge variant={typeVariants[type] || 'secondary'}>
          {typeLabels[type] || type}
        </Badge>
      )
    },
    meta: { width: '120px' },
  },
  {
    id: 'page',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Page" />
    ),
    cell: ({ row }) => <PageCell row={row} />,
    meta: { width: '150px' },
  },
  {
    accessorKey: 'createdAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Created" />
    ),
    cell: ({ row }) => {
      const date = row.getValue('createdAt') as string
      if (!date) return null
      return (
        <div className="flex items-center">
          {format(new Date(date), 'MMM d, yyyy')}
        </div>
      )
    },
    meta: { width: '120px' },
  },
]
